import type { Brand, Model } from '@studio/models/device';
import { isNotNil } from '@tryiris-ai/js-types';
import { mutex } from './constants.js';
import type { Collection } from '#~/api/device/registry/index.js';
import { fetchModels } from '#~/api/device/registry/index.js';
import { depot } from '#~/depot/index.js';

/**
 * Returns the models for the specified brand.
 *
 * @param brandId - The ID of the brand whose models are to be fetched.
 * @param token - Access token.
 * @returns The models in the device registry.
 */
export const getModels = async (
  brandId: Brand['id'],
  token: string
): Promise<Collection<Model>> => {
  const { produce, registry } = depot.getState();

  // Have we already fetched the models for this brand?
  if (isNotNil(registry.models[brandId])) {
    return registry.models[brandId]!;
  }

  // Fetch the models.
  return mutex.runExclusive(async () => {
    const models = depot.getState().registry.models[brandId];

    // Do we still need to run?
    if (isNotNil(models)) {
      return models;
    }

    // Fetch the device models.
    const result = await fetchModels(brandId, token);

    produce((draft) => {
      draft.registry.models[brandId] = result;
    });

    return result;
  });
};
